'use client';

import { SiteFooter } from '@/components/layouts/site-footer';

import { useFooterDescription } from '../api';

interface FooterDescriptionProps {
  /** Forwarded to `SiteFooter` — used by the admin preview path to
   *  bypass the network fetch while still routing through the same
   *  component. Mirrors the `FaqPage` / `PrivacyPage` pattern. */
  description?: string;
}

/**
 * Thin wrapper that owns the `useFooterDescription` query and renders
 * `SiteFooter` with the resolved description string. Falls back to
 * `undefined` (and therefore `SiteFooter`'s hard-coded copy) while the
 * query is loading or when the backend has no record yet, so the
 * layout never flickers to an empty state.
 */
export function FooterDescription({ description: initialDescription }: FooterDescriptionProps) {
  const hasServerDescription = initialDescription !== undefined;
  const { data } = useFooterDescription(undefined, { enabled: !hasServerDescription });

  // Priority: explicit prop (server/admin preview) > API row > static fallback
  // inside `SiteFooter`. Trim to avoid blank-line-only renders.
  const fromApi = typeof data?.description === 'string' ? data.description.trim() : '';
  const resolved =
    (typeof initialDescription === 'string' && initialDescription.trim()) || fromApi || undefined;

  return <SiteFooter description={resolved} />;
}
